import { useEffect, useRef, useState } from "react";
import { useParams } from "wouter";
import { CheckCircle, Clock3, FileSignature, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { trpc } from "../lib/trpc";

function SignatureCanvas({ onSignature }: { onSignature: (base64: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);

  const prepararCanvas = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = "#000000";
    context.lineWidth = 3;
    context.lineCap = "round";
  };

  useEffect(() => prepararCanvas(), []);

  const posicao = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) * (canvas.width / rect.width),
      y: (event.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const iniciar = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    const context = canvasRef.current?.getContext("2d");
    if (!context) return;
    drawingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    const ponto = posicao(event);
    context.beginPath();
    context.moveTo(ponto.x, ponto.y);
  };

  const desenhar = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const context = canvasRef.current?.getContext("2d");
    if (!context) return;
    const ponto = posicao(event);
    context.lineTo(ponto.x, ponto.y);
    context.stroke();
  };

  const finalizar = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas) onSignature(canvas.toDataURL("image/png"));
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={600}
        height={240}
        className="w-full touch-none rounded border bg-white"
        onPointerDown={iniciar}
        onPointerMove={desenhar}
        onPointerUp={finalizar}
        onPointerCancel={finalizar}
      />
      <Button type="button" variant="ghost" size="sm" className="mt-1 px-0 text-xs" onClick={() => { prepararCanvas(); onSignature(""); }}>Limpar assinatura</Button>
    </div>
  );
}

function formatarWhatsApp(valor: string) {
  const numeros = valor.replace(/\D/g, "").slice(0, 11);
  if (numeros.length <= 2) return numeros ? `(${numeros}` : "";
  if (numeros.length <= 6) return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
  if (numeros.length <= 10) return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`;
  return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
}

export default function AssinaturaPublica() {
  const { token = "" } = useParams<{ token: string }>();
  const [nome, setNome] = useState("");
  const [documento, setDocumento] = useState("");
  const [placa, setPlaca] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [assinaturaBase64, setAssinaturaBase64] = useState("");
  const [mensagemErro, setMensagemErro] = useState("");
  const [concluido, setConcluido] = useState(false);
  const assinaturaQuery = trpc.assinaturaPublica.obter.useQuery({ token }, { enabled: token.length === 48, retry: false, refetchInterval: 5000 });
  const assinarMutation = trpc.assinaturaPublica.assinar.useMutation();
  const sessao = assinaturaQuery.data;
  const tipo = sessao?.tipo;
  const contexto = sessao?.contexto as { titulo?: string; descricao?: string } | undefined;
  const exigePlaca = tipo === "EXPEDICAO" || tipo === "ROMANEIO";
  const exibeWhatsapp = tipo === "DEVOLUCAO" || tipo === "ROMANEIO";
  const documentoLabel = tipo === "EXPEDICAO" ? "Documento do motorista" : tipo === "ROMANEIO" ? "RG do motorista" : "RG do recebedor";
  const nomeLabel = tipo === "DEVOLUCAO" ? "Nome do recebedor" : "Nome completo do motorista";

  const assinar = async (event: React.FormEvent) => {
    event.preventDefault();
    setMensagemErro("");
    if (!nome.trim() || !documento.trim() || !assinaturaBase64 || (exigePlaca && !placa.trim())) {
      setMensagemErro("Preencha todos os campos obrigatórios e assine.");
      return;
    }
    try {
      await assinarMutation.mutateAsync({
        token,
        nomeAssinante: nome.trim(),
        documentoAssinante: documento.trim(),
        placaVeiculo: exigePlaca ? placa.trim().toUpperCase() : undefined,
        whatsapp: exibeWhatsapp ? whatsapp : undefined,
        assinaturaBase64,
      });
      setConcluido(true);
    } catch (error: any) {
      setMensagemErro(error?.message || "Não foi possível enviar a assinatura.");
    }
  };

  if (token.length !== 48 || assinaturaQuery.isError) {
    return <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4"><section className="w-full max-w-md rounded-xl border bg-white p-6 text-center shadow-sm"><Clock3 className="mx-auto h-9 w-9 text-red-500" /><h1 className="mt-3 text-lg font-semibold">QR inválido</h1><p className="mt-2 text-sm text-muted-foreground">Abra um novo QR pela tela interna.</p></section></main>;
  }

  if (assinaturaQuery.isLoading || !sessao) {
    return <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Carregando assinatura...</main>;
  }

  if (sessao.status === "EXPIRADO") {
    return <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4"><section className="w-full max-w-md rounded-xl border border-amber-200 bg-white p-6 text-center shadow-sm"><Clock3 className="mx-auto h-9 w-9 text-amber-600" /><h1 className="mt-3 text-lg font-semibold">QR expirado</h1><p className="mt-2 text-sm text-muted-foreground">Peça para o operador gerar um novo QR na tela interna.</p></section></main>;
  }

  if (concluido || sessao.status === "ASSINADO") {
    return <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4"><section className="w-full max-w-md rounded-xl border border-green-200 bg-white p-6 text-center shadow-sm"><CheckCircle className="mx-auto h-10 w-10 text-green-600" /><h1 className="mt-3 text-lg font-semibold">Assinatura enviada</h1><p className="mt-2 text-sm text-muted-foreground">O operador já pode conferir os dados e concluir o processo.</p></section></main>;
  }

  return (
    <main className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <section className="mx-auto w-full max-w-xl rounded-xl border bg-white p-5 shadow-sm sm:p-7">
        <div className="flex items-start gap-3 border-b pb-4">
          <div className="rounded-full bg-orange-100 p-2 text-orange-700"><FileSignature className="h-6 w-6" /></div>
          <div><h1 className="text-lg font-semibold">{contexto?.titulo || "Assinatura"}</h1><p className="mt-1 text-sm text-muted-foreground">{contexto?.descricao}</p></div>
        </div>
        <form className="mt-5 space-y-4" onSubmit={assinar}>
          <div><label className="text-sm font-medium">{nomeLabel}</label><Input value={nome} onChange={(event) => setNome(event.target.value)} autoComplete="name" /></div>
          <div><label className="text-sm font-medium">{documentoLabel}</label><Input value={documento} onChange={(event) => setDocumento(event.target.value)} /></div>
          {exigePlaca && <div><label className="text-sm font-medium">Placa do veículo</label><Input value={placa} onChange={(event) => setPlaca(event.target.value.toUpperCase())} placeholder="ABC-1234" /></div>}
          {exibeWhatsapp && <div><label className="text-sm font-medium">WhatsApp{tipo === "ROMANEIO" ? " (opcional)" : ""}</label><Input value={whatsapp} onChange={(event) => setWhatsapp(formatarWhatsApp(event.target.value))} placeholder="(11) 99999-9999" inputMode="tel" /></div>}
          <div><label className="text-sm font-medium">Assinatura digital</label><p className="mb-2 text-xs text-muted-foreground">Assine usando o dedo ou a caneta do aparelho.</p><SignatureCanvas onSignature={setAssinaturaBase64} /></div>
          {mensagemErro && <p className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{mensagemErro}</p>}
          <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" disabled={assinarMutation.isPending}>{assinarMutation.isPending ? "Enviando..." : "Enviar assinatura"}</Button>
        </form>
      </section>
    </main>
  );
}
